import saveAs from "file-saver";
import config from "../config.json";
import { IPageable, Params } from "../types/table/interfaces.type";
import { createAxiosInstance } from "./ServicesHelpers";
import { useQuery, UseQueryResult } from "@tanstack/react-query";

const axiosInstance = createAxiosInstance(config.ADMIN_API_BASE_URL);

export interface SenderTranslationType {
  id: string;
  type: string;
  name: string;
  isDeleted: boolean;
  effectiveDate: Date;
  translatedSenderId: string;
  conditionValues: ConditionValueType[];
  routingPlanId: string;
  operatorId: number;
  providerId: string;
  createdBy: string;
  position?: string;
  logicalOperator?: string;
  updatedBy?: string;
  cacheTtlSeconds?: number | null;
  randomLength?: number | null;
}
export interface ConditionValueType {
  conditionType: "EQUAL" | string;
  value: string;
  logicalOperator?: string;
}

export interface SenderHistory {
  id: string;
  snapshot: SenderTranslationType & { timestamp: string };
  historyTimestamp: string;
  mainId: number;
  createdBy: string;
}

export const exportSenderTranslation = async (
  params: Params<SenderTranslationType> = {}
): Promise<void> => {
  try {
    const response = await axiosInstance.get("/sender-translation/csv", {
      params,
      responseType: "blob",
    });

    const disposition = response.headers["content-disposition"];
    let filename = "SenderTranslation.csv";
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, "").trim();
      }
    }

    const blob = new Blob([response.data], { type: "text/csv" });
    saveAs(blob, filename);

    console.log("Export successful and file downloaded.");
  } catch (error: any) {
    console.error(
      "Error fetching SenderTranslation :",
      error.response?.data?.message || error.message || error
    );
  }
};
export async function fetchSenderTranslations(
  params: Params<SenderTranslationType>
): Promise<IPageable<SenderTranslationType>> {
  try {
    const response = await axiosInstance.get("/sender-translation", { params });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while getting the users."
    );
  }
}

export async function fetchSenderTranslation(
  id: SenderTranslationType["id"]
): Promise<SenderTranslationType> {
  try {
    const response = await axiosInstance.get(`/sender-translation/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting routing-plan/${id}.`
    );
  }
}

export const addSenderTranslationApi = async (
  data: Partial<SenderTranslationType>
) => {
  try {
    const response = await axiosInstance.post("/sender-translation", data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while adding the Sender Routing."
    );
  }
};
export const SenderTranslationBulkApi = async (
  data: Partial<SenderTranslationType>[]
) => {
  try {
     const Data = data.map(({ id, ...rest }) => rest);
    const response = await axiosInstance.put("/sender-translation", Data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while adding the Sender Routing."
    );
  }
};

export const SenderTranslationCreateAllApi = async (
  data: Partial<SenderTranslationType>[]
) => {
  try {
    const Data = data.map(({ id, ...rest }) => rest);
    const response = await axiosInstance.post("/sender-translation/all", Data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while adding the Sender Routing."
    );
  }
};

export const editSenderTranslationApi = async (
  id: string,
  data: Partial<SenderTranslationType>
) => {
  try {
    const response = await axiosInstance.put(`/sender-translation/${id}`, data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while updating the Sender Routing."
    );
  }
};

export const getSenderTranslationById = async (id: string) => {
  try {
    const response = await axiosInstance.get(`/sender-translation/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching the Sender Routing."
    );
  }
};

export const deleteSenderTranslationApi = async (
  ids: string[]
): Promise<void> => {
  try {
    await axiosInstance.delete(`/sender-translation`, { data: ids });
    console.log(`Sender Routing with ID ${ids} deleted successfully.`);
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while deleting the Sender Routing."
    );
  }
};

export const useSenderTranslationQuery = (
  params: Params<SenderTranslationType>
): UseQueryResult<IPageable<SenderTranslationType>, Error> => {
    const effectiveParams = {
    ...params,
    sort: params.sort || "createdDate,desc",
  };
  const queryKey = ["senderTranslation", JSON.stringify(effectiveParams)];
  return useQuery({
    queryKey,
    queryFn: () => fetchSenderTranslations(effectiveParams),
  });
};

export async function fetchSenderHistory(
  id: SenderTranslationType["id"],
  params: Params<SenderTranslationType> = {}
): Promise<IPageable<SenderTranslationType>> {
  try {
    const response = await axiosInstance.get(`/instructions/history`, { params: { mainId: id, sort: "historyTimestamp,desc", ...params } });
    const data = response.data;
   return {
      ...data,
      content: data.content.map((item: any) => ({
        ...item.snapshot,
        historyTimestamp: item.historyTimestamp
      })),
    };
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting the FallBack Routing history for ID ${id}.`
    );
  }
}

